import React, { useEffect, useMemo, useRef, useState } from "react";
import { Country, State, City } from "country-state-city";
import {
  getMyProfile,
  updateMyProfile,
  changeMyPassword,
} from "services/hrms.api";
import Loading from "components/common/Loading";

export default function ProfileOverview() {
  const fileRef = useRef(null);
  const [msg, setMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    country: "",
    state: "",
    city: "",
    postal: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
    avatar: null,
    avatarUrl: "",
  });

  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);

  // avatar preview
  const localPreview = useMemo(
    () => (form.avatar ? URL.createObjectURL(form.avatar) : ""),
    [form.avatar]
  );
  useEffect(() => {
    return () => {
      if (localPreview) URL.revokeObjectURL(localPreview);
    };
  }, [localPreview]);

  const displayAvatar = localPreview || form.avatarUrl || "";

  // Load profile
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setMsg("");
        const res = await getMyProfile();
        const u = res?.user || res;
        setMe(u);
        setForm((p) => ({
          ...p,
          firstName: u.firstName || "",
          lastName: u.lastName || "",
          email: u.email || "",
          phone: u.phone || "",
          address: u.address || "",
          country: (u.country || "").toUpperCase(),
          state: (u.state || "").toUpperCase(),
          city: u.city || "",
          postal: u.postal || "",
          avatarUrl: u.avatarUrl || "",
        }));
      } catch (e) {
        console.error("Profile load failed", e);
        setMsg("Could not load profile.");
      } finally {
        setLoading(false); // ⬅️ end
      }
    })();
  }, []);

  const onChange = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (file) setForm((p) => ({ ...p, avatar: file }));
  };

  const resetAvatar = () => {
    setForm((p) => ({ ...p, avatar: null }));
    if (fileRef.current) fileRef.current.value = "";
  };

  const onCancel = () => {
    if (!me) return;
    setForm((p) => ({
      ...p,
      firstName: me.firstName || "",
      lastName: me.lastName || "",
      email: me.email || "",
      phone: me.phone || "",
      address: me.address || "",
      country: (me.country || "").toUpperCase(),
      state: (me.state || "").toUpperCase(),
      city: me.city || "",
      postal: me.postal || "",
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
      avatar: null,
      avatarUrl: me.avatarUrl || "",
    }));
  };

  const onSave = async (e) => {
    e.preventDefault();
    setMsg("");

    if (form.newPassword && form.newPassword !== form.confirmPassword) {
      setMsg("New password and Confirm password do not match.");
      return;
    }

    setSubmitting(true);
    try {
      let updated;
      const hasFile = !!form.avatar;

      if (hasFile) {
        const fd = new FormData();
        fd.append("firstName", form.firstName);
        fd.append("lastName", form.lastName);
        fd.append("email", form.email);
        fd.append("phone", form.phone);
        fd.append("address", form.address);
        fd.append("country", form.country);
        fd.append("state", form.state);
        fd.append("city", form.city);
        fd.append("postal", form.postal);
        fd.append("avatar", form.avatar);
        updated = await updateMyProfile(fd, { multipart: true });
      } else {
        updated = await updateMyProfile({
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          phone: form.phone,
          address: form.address,
          country: form.country,
          state: form.state,
          city: form.city,
          postal: form.postal,
        });
      }

      const u = updated?.user || updated;
      setMe(u);
      setForm((p) => ({
        ...p,
        firstName: u.firstName || "",
        lastName: u.lastName || "",
        email: u.email || "",
        phone: u.phone || "",
        address: u.address || "",
        country: (u.country || "").toUpperCase(),
        state: (u.state || "").toUpperCase(),
        city: u.city || "",
        postal: u.postal || "",
        avatar: null,
        avatarUrl: u.avatarUrl || "",
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      }));
      if (fileRef.current) fileRef.current.value = "";

      if (form.currentPassword && form.newPassword) {
        await changeMyPassword({
          currentPassword: form.currentPassword,
          newPassword: form.newPassword,
        });
      }

      setMsg("Profile saved successfully.");
    } catch (err) {
      console.error(err);
      setMsg(err?.response?.data?.message || "Save failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !me) {
    return (
      <div className="p-4 md:p-6">
        <Loading variant="dots" fullscreen />
      </div>
    );
  }

  return (
    <div className="space-y-5 p-4 md:p-6">
      <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/10 dark:bg-navy-800">
        <div className="border-b border-gray-200 px-5 py-3 text-lg font-semibold text-gray-800 dark:border-white/10 dark:text-white">
          Profile
        </div>

        <form onSubmit={onSave} className="px-5 pb-5">
          {/* --- Basic Information --- */}
          <SectionTitle>Basic Information</SectionTitle>

          <div className="mb-6 rounded-xl bg-gray-50 p-4 dark:bg-white/5">
            <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
              <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-white ring-1 ring-gray-200 dark:bg-navy-900 dark:ring-white/10">
                {displayAvatar ? (
                  <img
                    src={displayAvatar}
                    alt="Profile"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="text-xs text-gray-400">40×40</span>
                )}
              </div>

              <div>
                <div className="text-sm font-semibold text-gray-800 dark:text-white">
                  Profile Photo
                </div>
                <p className="text-xs text-gray-500">
                  Recommended image size is 40px × 40px
                </p>
                <div className="mt-2 flex gap-3">
                  <label className="inline-flex cursor-pointer items-center rounded-md bg-gradient-to-r from-yellow-500 to-red-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-600">
                    Upload
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={onFile}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={resetAvatar}
                    className="text-xs font-semibold text-gray-600 hover:underline dark:text-white/80"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* --- Names / Email / Phone --- */}
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="First Name">
              <input
                name="firstName"
                value={form.firstName}
                onChange={onChange}
                className="dark:bg-transparent w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-white/10 dark:bg-navy-800 dark:text-white"
              />
            </Field>
            <Field label="Last Name">
              <input
                name="lastName"
                value={form.lastName}
                onChange={onChange}
                className="dark:bg-transparent w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-white/10 dark:bg-navy-800 dark:text-white"
              />
            </Field>
            <Field label="Email">
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={onChange}
                className="dark:bg-transparent w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-white/10 dark:bg-navy-800 dark:text-white"
              />
            </Field>
            <Field label="Phone">
              <input
                name="phone"
                value={form.phone}
                onChange={onChange}
                className="dark:bg-transparent w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-white/10 dark:bg-navy-800 dark:text-white"
              />
            </Field>
          </div>

          {/* --- Address --- */}
          <SectionTitle className="mt-6">Address Information</SectionTitle>

          <Field label="Address">
            <input
              name="address"
              value={form.address}
              onChange={onChange}
              className="dark:bg-transparent w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-white/10 dark:bg-navy-800 dark:text-white"
            />
          </Field>

          <div className="grid gap-4 md:grid-cols-2">
            {/* Country */}
            <Field label="Country">
              <select
                name="country"
                value={form.country}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    country: e.target.value,
                    state: "",
                    city: "",
                  }))
                }
                className="dark:bg-transparent w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-white/10 dark:bg-navy-800 dark:text-white"
              >
                <option value="">Select Country</option>
                {Country.getAllCountries().map((c) => (
                  <option key={c.isoCode} value={c.isoCode}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>

            {/* State */}
            <Field label="State">
              <select
                name="state"
                value={form.state}
                onChange={(e) =>
                  setForm((p) => ({ ...p, state: e.target.value, city: "" }))
                }
                disabled={!form.country}
                className="dark:bg-transparent w-full rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:opacity-50 dark:border-white/10 dark:bg-navy-800 dark:text-white"
              >
                <option value="">Select State</option>
                {form.state &&
                  !State.getStatesOfCountry(form.country).some(
                    (s) => s.isoCode === form.state
                  ) && <option value={form.state}>{form.state}</option>}
                {form.country &&
                  State.getStatesOfCountry(form.country).map((s) => (
                    <option key={s.isoCode} value={s.isoCode}>
                      {s.name}
                    </option>
                  ))}
              </select>
            </Field>

            {/* City */}
            <Field label="City">
              <select
                name="city"
                value={form.city}
                onChange={(e) =>
                  setForm((p) => ({ ...p, city: e.target.value }))
                }
                disabled={!form.state}
                className="dark:bg-transparent w-full rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:opacity-50 dark:border-white/10 dark:bg-navy-800 dark:text-white"
              >
                <option value="">Select City</option>
                {form.city &&
                  !City.getCitiesOfState(form.country, form.state).some(
                    (c) => c.name === form.city
                  ) && <option value={form.city}>{form.city}</option>}
                {form.state &&
                  City.getCitiesOfState(form.country, form.state).map((c) => (
                    <option key={c.name} value={c.name}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </Field>

            <Field label="Postal Code">
              <input
                name="postal"
                value={form.postal}
                onChange={onChange}
                className="dark:bg-transparent w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-white/10 dark:bg-navy-800 dark:text-white"
              />
            </Field>
          </div>

          {/* --- Change Password --- */}
          <SectionTitle className="mt-6">Change Password</SectionTitle>

          <div className="grid gap-4 md:grid-cols-3">
            {[
              [
                "Current Password",
                "currentPassword",
                showCurrent,
                setShowCurrent,
              ],
              ["New Password", "newPassword", showNew, setShowNew],
              [
                "Confirm Password",
                "confirmPassword",
                showConfirm,
                setShowConfirm,
              ],
            ].map(([label, name, visible, toggle]) => (
              <Field key={name} label={label}>
                <div className="relative">
                  <input
                    type={visible ? "text" : "password"}
                    name={name}
                    value={form[name]}
                    onChange={onChange}
                    className="dark:bg-transparent w-full rounded-lg border border-gray-300 px-3 py-2 pr-9 text-sm dark:border-white/10 dark:bg-navy-800 dark:text-white"
                  />
                  <button
                    type="button"
                    onClick={() => toggle((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {visible ? "🙈" : "👁️"}
                  </button>
                </div>
              </Field>
            ))}
          </div>

          {/* Footer */}
          <div className="mt-6 flex items-center justify-between">
            <span className="text-xs text-gray-500">{msg}</span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onCancel}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-white/10 dark:text-white/80 dark:hover:bg-white/10"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="rounded-lg bg-gradient-to-r from-yellow-500 to-red-500 px-5 py-2 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-60"
              >
                {submitting ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ---------- small helpers ---------- */
function SectionTitle({ children, className = "" }) {
  return (
    <div
      className={`mb-3 mt-4 border-b border-gray-200 pb-2 text-sm font-semibold text-gray-700 dark:border-white/10 dark:text-white ${className}`}
    >
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-3">
      <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-white">
        {label}
      </label>
      {children}
    </div>
  );
}
